import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MainTemplate } from 'templates';
import { LoadingIndicator } from 'atoms';
import {
  useAllExpenses,
  useExpenseCategories,
  usePopupStateOperations,
  IExpensesFilters,
} from 'core/hooks';
import { PopupNames } from 'core';
import {
  DateRangePresets,
  IDateRangeValue,
  IExpenseFormPopupData,
  presetRange,
} from 'molecules';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import { ExpensesFilterBar } from './ExpensesFilterBar';
import { ExpensesTable } from './ExpensesTable';

const DEFAULT_RANGE: IDateRangeValue = {
  preset: 'thisMonth',
  ...presetRange('thisMonth'),
};

const buildDefaultFilters = (
  range: IDateRangeValue = DEFAULT_RANGE,
): IExpensesFilters => ({
  dateFrom: range.from,
  dateTo: range.to,
});

/**
 * Expenses register (TZ §3.5). State stays minimal: filters + a click-to-open
 * popup for create/edit. KPI total is computed client-side from `amount`
 * over the loaded page (Phase 5: schema is currency-neutral, no more BYN
 * equivalent) — good enough for the page-level total in PLN.
 */
export const FakturowniaExpenses = memo(() => {
  const { t } = useTranslation();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const [range, setRange] = useState<IDateRangeValue>(DEFAULT_RANGE);
  const [filters, setFilters] = useState<IExpensesFilters>(() =>
    buildDefaultFilters(),
  );

  const { data, loading, fetching, permissionDenied, refetch } = useAllExpenses(
    filters,
  );
  const { categories } = useExpenseCategories();

  const onRangeChange = useCallback((next: IDateRangeValue) => {
    setRange(next);
    setFilters((prev) => ({
      ...prev,
      dateFrom: next.from,
      dateTo: next.to,
    }));
  }, []);

  const onReset = useCallback(() => {
    setRange(DEFAULT_RANGE);
    setFilters(buildDefaultFilters());
  }, []);

  const onAdd = useCallback(() => {
    const payload: IExpenseFormPopupData = { onSaved: refetch };
    showPopup(PopupNames.expenseForm, payload);
  }, [showPopup, refetch]);

  const onRowClick = useCallback(
    (expenseId: string) => {
      const payload: IExpenseFormPopupData = {
        expenseId,
        onSaved: refetch,
      };
      showPopup(PopupNames.expenseForm, payload);
    },
    [showPopup, refetch],
  );

  const totalPln = useMemo(
    () => data.reduce((sum, row) => sum + (row.amount || 0), 0),
    [data],
  );

  if (permissionDenied) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.PageTitle>{t('pages.fakturownia.expenses.title')}</S.PageTitle>
          <S.EmptyState>
            {t('pages.fakturownia.expenses.noAccess')}
          </S.EmptyState>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  if (loading && data.length === 0) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.CenterLoader>
            <LoadingIndicator />
          </S.CenterLoader>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  const isEmpty = data.length === 0;

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.HeaderRow>
          <S.PageTitle>{t('pages.fakturownia.expenses.title')}</S.PageTitle>
          <S.HeaderRight>
            <KpiPill>
              {t('pages.fakturownia.expenses.kpiTotal')}:{' '}
              <strong>{totalPln.toFixed(2)} PLN</strong>
            </KpiPill>
            <AddButton type="button" onClick={onAdd}>
              {t('pages.fakturownia.expenses.addNew')}
            </AddButton>
          </S.HeaderRight>
        </S.HeaderRow>
        <S.SubTitle>{t('pages.fakturownia.expenses.subtitle')}</S.SubTitle>

        <DateRangePresets value={range} onChange={onRangeChange} />

        <ExpensesFilterBar
          filters={filters}
          onChange={setFilters}
          onReset={onReset}
          categories={categories}
        />

        {isEmpty ? (
          <S.EmptyState>{t('pages.fakturownia.expenses.empty')}</S.EmptyState>
        ) : (
          <S.DimWhenFetching fetching={fetching}>
            <ExpensesTable rows={data} onRowClick={onRowClick} />
          </S.DimWhenFetching>
        )}
      </S.Wrapper>
    </MainTemplate>
  );
});

const KpiPill = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '6px 12px',
      borderRadius: 999,
      background: '#f1f5f9',
      color: '#475569',
      fontSize: 12,
      fontWeight: 600,
    }}
  >
    {children}
  </span>
);

const AddButton = (props: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    {...props}
    style={{
      border: '1px solid #2563eb',
      background: '#2563eb',
      color: '#fff',
      padding: '6px 14px',
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
    }}
  />
);
